import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Typography,
} from '@mui/material';
import AllInclusiveRoundedIcon    from '@mui/icons-material/AllInclusiveRounded';
import ArrowBackIosNewRoundedIcon  from '@mui/icons-material/ArrowBackIosNewRounded';
import AutoAwesomeRoundedIcon      from '@mui/icons-material/AutoAwesomeRounded';
import AutoFixHighRoundedIcon      from '@mui/icons-material/AutoFixHighRounded';
import BoltRoundedIcon             from '@mui/icons-material/BoltRounded';
import CheckRoundedIcon            from '@mui/icons-material/CheckRounded';
import DiamondRoundedIcon          from '@mui/icons-material/DiamondRounded';
import ExpandMoreRoundedIcon       from '@mui/icons-material/ExpandMoreRounded';
import LockRoundedIcon             from '@mui/icons-material/LockRounded';
import NotesRoundedIcon            from '@mui/icons-material/NotesRounded';
import StarRoundedIcon             from '@mui/icons-material/StarRounded';
import SupportAgentRoundedIcon     from '@mui/icons-material/SupportAgentRounded';
import VerifiedRoundedIcon         from '@mui/icons-material/VerifiedRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';

import { GetPlans, CreateOrder, VerifyAndActivate, CancelSubscription } from '../../redux/actions/billingActions';
import {
  trackUpgradeClick,
  trackBillingToggle,
  trackFAQExpand,
  trackPaymentInitiated,
  trackPaymentSuccess,
} from '../../utils/analytics';

import './styles.scss';

const FAQS = [
  {
    q: "What's included in the Basic plan?",
    a: `The Basic plan gives you up to 3 notes with the full rich-text block editor and all content block types. No credit card required — ever.`,
  },
  {
    q: 'What does "lifetime" mean for Pro?',
    a: 'One payment, yours forever. You pay ₹199 once and get unlimited notes and all future updates — no recurring charges.',
  },
  {
    q: 'Can I upgrade from Basic to Pro later?',
    a: 'Absolutely. You can upgrade at any time from this page.',
  },
  {
    q: 'What payment methods are accepted?',
    a: 'All major credit and debit cards, UPI, net banking, and wallets — processed securely via Razorpay.',
  },
  {
    q: 'Is my data safe?',
    a: 'Yes. Your notes are encrypted at rest and in transit. We never sell your data.',
  },
  {
    q: 'When is the AI writing assistant coming to Pro Plus?',
    a: 'We are actively building it. Pro Plus subscribers will get it the moment it launches, at no extra cost.',
  },
  {
    q: 'Can I get a refund?',
    a: 'All purchases are final. We do not offer refunds after payment is completed. Please review the plan details carefully before purchasing.',
  },
  {
    q: 'What currencies are supported?',
    a: 'Prices are in Indian Rupees (INR). Razorpay handles secure payment processing for Indian customers.',
  },
];

//  Helpers 

const formatINR = (num) =>
  '\u20B9' + Number(num).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const getPlanFeatures = (plan) => {
  switch (plan.payment_type) {
    case 'free':
      return [
        { icon: <NotesRoundedIcon sx={{ fontSize: 17 }} />,  text: `Up to ${plan.max_notes} notes`,  dimmed: false },
        { icon: <CheckRoundedIcon sx={{ fontSize: 17 }} />,  text: 'Rich text block editor',          dimmed: false },
        { icon: <CheckRoundedIcon sx={{ fontSize: 17 }} />,  text: 'All content block types',         dimmed: false },
        { icon: <CheckRoundedIcon sx={{ fontSize: 17 }} />,  text: 'Google Sign-in',                  dimmed: false },
      ];
    case 'one_time':
      return [
        { icon: <AllInclusiveRoundedIcon sx={{ fontSize: 17 }} />, text: 'Unlimited notes',            dimmed: false },
        { icon: <CheckRoundedIcon sx={{ fontSize: 17 }} />,        text: 'Everything in Basic',        dimmed: false },
        { icon: <BoltRoundedIcon sx={{ fontSize: 17 }} />,         text: 'One-time · Never pay again', dimmed: false },
        { icon: <WorkspacePremiumRoundedIcon sx={{ fontSize: 17 }} />, text: 'Lifetime access',         dimmed: false },
        { icon: <CheckRoundedIcon sx={{ fontSize: 17 }} />,        text: 'All future updates',         dimmed: false },
      ];
    case 'recurring':
      return [
        { icon: <AllInclusiveRoundedIcon sx={{ fontSize: 17 }} />, text: 'Unlimited notes',              dimmed: false },
        { icon: <CheckRoundedIcon sx={{ fontSize: 17 }} />,        text: 'Everything in Pro',            dimmed: false },
        { icon: <AutoFixHighRoundedIcon sx={{ fontSize: 17 }} />,  text: 'AI Writing Assistant œ¦',       dimmed: true  },
        { icon: <SupportAgentRoundedIcon sx={{ fontSize: 17 }} />, text: 'Priority support',             dimmed: false },
        { icon: <AutoAwesomeRoundedIcon sx={{ fontSize: 17 }} />,  text: 'Early access to new features', dimmed: false },
      ];
    default:
      return [];
  }
};

const ALL_PLANS_INCLUDE = [
  'Secure storage',
  'Dark & light mode',
  'Mobile friendly',
  'No ads, ever',
];

//  Main component 

const PricingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const plans    = useSelector(state => state.billing.plans);
  const user     = useSelector(state => state.auth.user);

  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loading, setLoading]             = useState(true);
  const [paying, setPaying]               = useState(null);
  const [cancelling, setCancelling]       = useState(false);
  const [expandedFaq, setExpandedFaq]     = useState(false);

  useEffect(() => {
    dispatch(GetPlans()).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async (plan) => {
    if (paying) return;
    setPaying(plan.id);
    trackUpgradeClick(plan.name, plan.payment_type);

    const isOneTime = plan.payment_type === 'one_time';
    const period    = isOneTime ? 'lifetime' : billingPeriod;

    try {
      // 1. Create a Razorpay order on the backend
      const order = await dispatch(CreateOrder(plan.id, period));
      // order = { order_id, internal_order_id, amount, currency, key_id }

      // 2. Load Razorpay checkout script if not already loaded
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload  = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      // 3. Open Razorpay widget
      trackPaymentInitiated(plan.name, order.amount / 100);
      const rzp = new window.Razorpay({
        key:         order.key_id,
        order_id:    order.order_id,
        amount:      order.amount,
        currency:    order.currency || 'INR',
        name:        'NoteCORE',
        description: plan.name,
        theme:       { color: '#7c3aed' },
        handler: async (response) => {
          // 4. Verify payment — send field names the backend expects
          try {
            await dispatch(VerifyAndActivate({
              internal_order_id:   order.internal_order_id,
              provider_order_id:   response.razorpay_order_id,
              provider_payment_id: response.razorpay_payment_id,
              provider_signature:  response.razorpay_signature,
            }));
            trackPaymentSuccess(plan.name, order.amount / 100);
          } catch (_) {
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setPaying(null);
          }
        },
        modal: {
          ondismiss: () => setPaying(null),
        },
      });
      rzp.open();
    } catch (_) {
      toast.error('Could not initiate payment. Please try again.');
      setPaying(null);
    }
  };

  const handleCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await dispatch(CancelSubscription());
    } catch (_) {
      toast.error('Could not cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const currentPlanSlug = user?.subscription?.plan?.slug || 'basic';

  // Pro Plus is temporarily disabled — only show free + one_time plans
  const orderedPlans = [...plans]
    .filter(p => p.payment_type !== 'recurring')
    .sort((a, b) => a.sort_order - b.sort_order);

  const hasRecurring = orderedPlans.some(p => p.payment_type === 'recurring');

  return (
    <>
      <Helmet>
        <title>Pricing &amp; Plans — NoteCORE</title>
        <meta name="description" content="Manage your NoteCORE plan. Upgrade to Pro for unlimited notes with a single one-time payment." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      {/*  Full-page ambient gradient background  */}
      <Box className="pricing-page-bg" aria-hidden="true">
        <Box className="pricing-page-bg__blob pricing-page-bg__blob--violet" />
        <Box className="pricing-page-bg__blob pricing-page-bg__blob--sky" />
        <Box className="pricing-page-bg__blob pricing-page-bg__blob--teal" />
      </Box>

    <Container maxWidth="md" sx={{ pb: 8, position: 'relative', zIndex: 1 }}>

      {/*  Back button  */}
      <Box pt={3} pb={1}>
        <Button
          startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: '16px !important' }} />}
          onClick={() => navigate('/app')}
          sx={{ fontWeight: 600, borderRadius: '10px', textTransform: 'none', fontSize: 14 }}
        >
          Back to Notes
        </Button>
      </Box>

      {/*  Hero  */}
      <Box className="pricing-hero">
        <Box className="pricing-hero__eyebrow">
          <AutoAwesomeRoundedIcon sx={{ fontSize: 13 }} />
          Simple, honest pricing
        </Box>

        <Typography
          variant="h3"
          component="h1"
          fontWeight={900}
          lineHeight={1.15}
          className="pricing-hero__title"
          sx={{ fontSize: { xs: '2rem', sm: '2.6rem' } }}
        >
          <span className="pricing-hero__gradient-text">One app.</span>
          <br />
          Two plans.
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          mt={2}
          className="pricing-hero__subtitle"
          textAlign="center"
          minWidth={"100%"}
        >
          Start free forever. Upgrade once when you need more  or subscribe
          for the full AI-powered experience.
        </Typography>
      </Box>

      {/*  Toggle  */}
      {hasRecurring && (
        <Box className="pricing-toggle">
          <Box className="pricing-toggle__pill">
            <Box
              className={`pricing-toggle__pill-track${billingPeriod === 'monthly' ? ' pricing-toggle__pill-track--left' : ' pricing-toggle__pill-track--right'}`}
            />
            <button
              className={`pricing-toggle__option${billingPeriod === 'monthly' ? ' pricing-toggle__option--active' : ''}`}
              onClick={() => { setBillingPeriod('monthly'); trackBillingToggle('monthly'); }}
            >
              Monthly
            </button>
            <button
              className={`pricing-toggle__option${billingPeriod === 'yearly' ? ' pricing-toggle__option--active' : ''}`}
              onClick={() => { setBillingPeriod('yearly'); trackBillingToggle('yearly'); }}
            >
              Yearly
            </button>
          </Box>
        </Box>
      )}

      {/*  Plan cards  */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress sx={{ color: '#7c3aed' }} />
        </Box>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 400px))' }}
          gap={3}
          alignItems="stretch"
          justifyContent="center"
        >
          {orderedPlans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              billingPeriod={billingPeriod}
              currentPlanSlug={currentPlanSlug}
              isCurrent={currentPlanSlug === plan.slug}
              onSubscribe={() => handleSubscribe(plan)}
              onCancel={handleCancel}
              paying={paying === plan.id}
              cancelling={cancelling}
            />
          ))}
        </Box>
      )}

      {/*  All plans include  */}
      {!loading && (
        <Box mt={4} className="all-plans-strip">
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: 1, opacity: 0.5, width: '100%', textAlign: 'center', mb: 0.5 }}
          >
            All plans include
          </Typography>
          {ALL_PLANS_INCLUDE.map(item => (
            <Box key={item} className="all-plans-strip__item">
              <CheckRoundedIcon sx={{ fontSize: 14, color: '#7c3aed' }} />
              <Typography variant="body2">{item}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/*  No-refund notice  */}
      {!loading && (
        <Box
          mt={3}
          p={2}
          sx={{
            border: '1px solid rgba(239,68,68,0.4)',
            borderRadius: '10px',
            bgcolor: 'rgba(239,68,68,0.06)',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ color: '#ef4444', letterSpacing: 0.3 }}>
            All purchases are final — no refunds after payment is completed.
          </Typography>
        </Box>
      )}

      {/*  Powered by Razorpay  */}
      {!loading && (
        <Box mt={2} display="flex" justifyContent="center" alignItems="center" gap={1} sx={{ opacity: 0.5 }}>
          <LockRoundedIcon sx={{ fontSize: 13 }} />
          <Typography variant="caption" fontWeight={600}>
            Payments powered by Razorpay · Cards, UPI &amp; Net Banking
          </Typography>
        </Box>
      )}

      {/*  FAQ  */}
      <Box mt={7}>
        <Typography variant="h5" component="h2" fontWeight={800} textAlign="center" mb={1}>
          Frequently asked questions
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" mb={4}>
          Still have questions?{' '}
          <Box component="a" href="mailto:contact@palindev.com" sx={{ color: '#7c3aed', fontWeight: 600 }}>
            Drop us a message
          </Box>
        </Typography>

        {FAQS.map((faq, i) => (
          <Accordion
            key={i}
            expanded={expandedFaq === i}
            onChange={(_, open) => { if (open) trackFAQExpand(faq.q); setExpandedFaq(open ? i : false); }}
            className="faq-accordion"
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
              <Typography variant="body1" fontWeight={600}>{faq.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" lineHeight={1.75}>{faq.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/*  Trust footer  */}
      <Box mt={6} className="trust-footer">
        <Box className="trust-footer__item">
          <LockRoundedIcon sx={{ fontSize: 14 }} />
          <span>Secure payments via Razorpay</span>
        </Box>
        <Box className="trust-footer__item">
          <VerifiedRoundedIcon sx={{ fontSize: 14 }} />
          <span>256-bit SSL encryption</span>
        </Box>
        <Box className="trust-footer__item">
          <span>No hidden fees</span>
        </Box>
      </Box>

    </Container>
    </>
  );
};

//  Plan Card 

const PlanCard = ({ plan, billingPeriod, currentPlanSlug, isCurrent, onSubscribe, onCancel, paying, cancelling }) => {
  const isFree      = plan.payment_type === 'free';
  const isOneTime   = plan.payment_type === 'one_time';
  const isRecurring = plan.payment_type === 'recurring';

  const features = getPlanFeatures(plan);

  const userOnProPlus            = currentPlanSlug === 'pro-plus';
  const isIncludedWithHigherPlan = isOneTime && userOnProPlus;
  const isCancel                 = isRecurring && userOnProPlus;

  const ctaDisabled = isFree || isCurrent || isIncludedWithHigherPlan || paying || cancelling;

  // Price computation
  let priceDisplay = null;
  let priceUnit    = null;
  let priceBadge   = null;

  if (isFree) {
    priceDisplay = 'Free';
    priceUnit    = 'forever';
  } else if (isOneTime) {
    priceDisplay = formatINR(plan.price_one_time || 0);
    priceUnit    = null;
    priceBadge   = { label: 'one-time', className: 'plan-card__price-badge--lifetime' };
  } else if (isRecurring) {
    const monthly = billingPeriod === 'monthly';
    priceDisplay  = formatINR(monthly ? (plan.price_monthly || 0) : (plan.price_yearly || 0));
    priceUnit     = `/ ${monthly ? 'mo' : 'yr'}`;
    if (!monthly) {
      const saved = (Math.round((parseFloat(plan.price_monthly || 0) * 12) - parseFloat(plan.price_yearly || 0)));
      priceBadge  = { label: `Save \u20B9${saved}`, className: 'plan-card__price-badge--yearly' };
    } else {
      priceBadge  = { label: 'billed monthly', className: 'plan-card__price-badge--monthly' };
    }
  }

  const cardClass = isFree ? 'plan-card--free' : isOneTime ? 'plan-card--one-time' : 'plan-card--recurring';

  const planIcon = isFree
    ? <NotesRoundedIcon sx={{ fontSize: 22, color: '#6b7280' }} />
    : isOneTime
      ? <StarRoundedIcon sx={{ fontSize: 22, color: '#7c3aed' }} />
      : <DiamondRoundedIcon sx={{ fontSize: 22, color: '#0ea5e9' }} />;

  const ctaLabel = (() => {
    if (isCurrent)                return 'Current plan';
    if (isFree)                   return 'Your starting plan';
    if (isIncludedWithHigherPlan) return 'Included with Pro Plus';
    if (isCancel)                 return cancelling ? 'Cancelling…' : 'Cancel Pro Plus';
    if (isOneTime)                return `Buy Pro · ${formatINR(plan.price_one_time || 0)}`;
    return `Get Pro Plus`;
  })();

  const handleClick = () => {
    if (ctaDisabled) return;
    if (isCancel) {
      onCancel();
    } else {
      onSubscribe();
    }
  };

  return (
    <Box
      className={`plan-card ${cardClass}`}
      sx={{ bgcolor: 'background.paper' }}
    >
      {isOneTime && (
        <Box className="plan-card__popular-badge">
          <StarRoundedIcon sx={{ fontSize: 11 }} />
          Best Value
        </Box>
      )}

      <Box className={`plan-card__plan-icon plan-card__plan-icon--${isFree ? 'free' : isOneTime ? 'one-time' : 'recurring'}`}>
        {planIcon}
      </Box>

      <Typography variant="h6" component="h3" fontWeight={800} mb={0.25}>
        {plan.name}
      </Typography>
      {plan.description && (
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {plan.description}
        </Typography>
      )}

      <Box className="plan-card__price-row">
        <Typography
          variant={priceDisplay === 'Free' ? 'h5' : 'h4'}
          fontWeight={900}
          color={isOneTime ? 'secondary.main' : 'text.primary'}
        >
          {priceDisplay}
        </Typography>
        {priceUnit && (
          <Typography variant="body2" color="text.secondary" ml={0.5}>
            {priceUnit}
          </Typography>
        )}
      </Box>
      {priceBadge && (
        <Box component="span" className={`plan-card__price-badge ${priceBadge.className}`}>
          {priceBadge.label}
        </Box>
      )}

      <Divider className="plan-card__divider" />

      <ul className="plan-card__feature-list">
        {features.map((f, i) => (
          <li key={i} className={`plan-card__feature-item${f.dimmed ? ' plan-card__feature-item--coming-soon' : ''}`}>
            <Box sx={{ color: isOneTime ? '#7c3aed' : isRecurring ? '#0ea5e9' : '#6b7280', flexShrink: 0 }}>
              {f.icon}
            </Box>
            <Typography variant="body2" fontWeight={500}>
              {f.text}
              {f.dimmed && (
                <Box component="span" sx={{ ml: 1, fontSize: 10, fontWeight: 700, color: '#0ea5e9', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Coming Soon
                </Box>
              )}
            </Typography>
          </li>
        ))}
      </ul>

      <Button
        variant={isOneTime && !isCurrent && !isIncludedWithHigherPlan ? 'contained' : 'outlined'}
        color={isCancel ? 'error' : isOneTime && !isIncludedWithHigherPlan ? 'secondary' : 'inherit'}
        fullWidth
        onClick={handleClick}
        disabled={ctaDisabled && !isCancel}
        className={`plan-card__cta${isOneTime && !isIncludedWithHigherPlan ? ' plan-card__cta--pro' : ''}`}
        sx={{
          ...(!isOneTime && !isCurrent && !isFree && !isIncludedWithHigherPlan && {
            borderColor: isCancel ? undefined : isRecurring ? '#0ea5e9' : undefined,
            color:       isCancel ? undefined : isRecurring ? '#0ea5e9' : undefined,
            '&:hover': {
              borderColor: isCancel ? undefined : isRecurring ? '#0ea5e9' : undefined,
              bgcolor:     isCancel ? undefined : isRecurring ? 'rgba(14,165,233,0.08)' : undefined,
            },
          }),
        }}
      >
        {(paying || (isCancel && cancelling))
          ? <CircularProgress size={18} color="inherit" />
          : ctaLabel}
      </Button>
    </Box>
  );
};

export default PricingPage;
